"use client";

import React, { useState, useEffect, useRef } from "react";
import { SectionContentProps } from "../../types";
import EditableText from "../../core/EditableText";
import { PiFacebookLogo, PiInstagramLogo, PiTwitterLogo, PiLinkedinLogo, PiArrowLeftBold, PiArrowRightBold } from "react-icons/pi";

interface TeamMember {
  name: string;
  role: string;
  image: string;
  facebook?: string;
  twitter?: string;
  instagram?: string;
  linkedin?: string;
}

export default function OvanTeam({
  content,
  onContentChange,
  isSelected,
}: SectionContentProps) {
  const fontFamily = content.fontFamily || "'Inter', sans-serif";
  const [isDark, setIsDark] = useState(true);
  const [currentIndex, setCurrentIndex] = useState(0);
  const [visibleCount, setVisibleCount] = useState(4);
  const autoPlayRef = useRef<ReturnType<typeof setInterval> | null>(null);
  const trackRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (typeof window !== "undefined") {
      const savedTheme = localStorage.getItem("ovan-theme") || "dark";
      setIsDark(savedTheme === "dark");
      const handleThemeChange = () => {
        setIsDark(localStorage.getItem("ovan-theme") === "dark");
      };
      window.addEventListener("ovan-theme-change", handleThemeChange);
      
      const handleResize = () => {
        if (window.innerWidth < 640) {
          setVisibleCount(1);
        } else if (window.innerWidth < 1024) {
          setVisibleCount(2);
        } else {
          setVisibleCount(4);
        }
      };
      handleResize();
      window.addEventListener("resize", handleResize);

      return () => {
        window.removeEventListener("ovan-theme-change", handleThemeChange);
        window.removeEventListener("resize", handleResize);
      };
    }
  }, []);

  const defaultMembers: TeamMember[] = [
    { name: "Calvin Carlo",    role: "CEO / Owner",  image: "/assets/1-BcvSHoAR.jpg" },
    { name: "Alisa Barrett",   role: "Designer",     image: "/assets/2-DcEn_wk3.jpg" },
    { name: "David Jackson",   role: "Developer",    image: "/assets/3-7jZOSvHE.jpg" },
    { name: "Sara Welch",      role: "Manager",      image: "/assets/4-BAaQ2Kp5.jpg" },
    { name: "Ramon Gibson",    role: "C.E.O",        image: "/assets/1-BcvSHoAR.jpg" },
    { name: "Megan Cade",      role: "Engineer",     image: "/assets/2-DcEn_wk3.jpg" },
    { name: "Stella Robinson", role: "Engineer",     image: "/assets/3-7jZOSvHE.jpg" },
    { name: "Paul Phelan",     role: "Engineer",     image: "/assets/4-BAaQ2Kp5.jpg" },
    { name: "Lena Morris",     role: "UI Designer",  image: "/assets/1-BcvSHoAR.jpg" },
    { name: "Jake Turner",     role: "Full Stack",   image: "/assets/2-DcEn_wk3.jpg" },
    { name: "Nina Patel",      role: "Product Lead", image: "/assets/3-7jZOSvHE.jpg" },
    { name: "Omar Shaikh",     role: "DevOps",       image: "/assets/4-BAaQ2Kp5.jpg" },
  ];

  const members: TeamMember[] = content.items || defaultMembers;

  const maxIndex = Math.max(0, members.length - visibleCount);

  const goTo = (idx: number) => {
    setCurrentIndex(Math.min(Math.max(idx, 0), maxIndex));
  };

  const startAutoPlay = () => {
    if (autoPlayRef.current) clearInterval(autoPlayRef.current);
    autoPlayRef.current = setInterval(() => {
      setCurrentIndex(prev => (prev >= maxIndex ? 0 : prev + 1));
    }, 3500);
  };

  useEffect(() => {
    // Disable autoplay in builder mode to prevent sliding while editing content
    if (onContentChange) {
      if (autoPlayRef.current) clearInterval(autoPlayRef.current);
      return;
    }
    startAutoPlay();
    return () => { if (autoPlayRef.current) clearInterval(autoPlayRef.current); };
  }, [members.length, maxIndex, onContentChange]);

  const handlePrev = () => {
    goTo(currentIndex > 0 ? currentIndex - 1 : maxIndex);
    if (!onContentChange) startAutoPlay();
  };

  const handleNext = () => {
    goTo(currentIndex < maxIndex ? currentIndex + 1 : 0);
    if (!onContentChange) startAutoPlay();
  };

  const updateMember = (idx: number, field: keyof TeamMember, value: string) => {
    if (!onContentChange) return;
    const newMembers = [...members];
    newMembers[idx] = { ...newMembers[idx], [field]: value };
    onContentChange({ ...content, items: newMembers });
  };

  const cardWidthPct = 100 / visibleCount;
  const translatePct = currentIndex * cardWidthPct;

  return (
    <section
      id="team"
      className={`py-16 md:py-24 transition-colors duration-200 ${
        isDark ? "bg-slate-900" : "bg-white"
      } ${isSelected ? "ring-2 ring-blue-500 relative z-20 shadow-md" : ""}`}
      style={{ fontFamily }}
    >
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">

        {/* Section Header */}
        <div className="text-center pb-12 max-w-xl mx-auto">
          <h2 className={`mb-4 text-2xl md:text-3xl leading-normal font-semibold transition-colors ${
            isDark ? "text-white" : "text-slate-900"
          }`}>
            <EditableText
              value={content.title || "Meet the Professional Team"}
              onChange={(val) => onContentChange && onContentChange({ ...content, title: val })}
              as="span"
              className={isDark ? "text-white" : "text-slate-900"}
              placeholder="Team Title"
            />
          </h2>
          <p className="text-slate-400 text-sm sm:text-base">
            <EditableText
              value={content.text || "Our design projects are fresh and simple and will benefit your business greatly. Learn more about our work!"}
              onChange={(val) => onContentChange && onContentChange({ ...content, text: val })}
              as="span"
              placeholder="Team Subtitle"
            />
          </p>
        </div>

        {/* Carousel wrapper */}
        <div className="relative">
          {/* Prev button */}
          <button
            onClick={handlePrev}
            className={`absolute left-0 top-1/2 -translate-y-1/2 -translate-x-4 z-10 size-10 rounded-full flex items-center justify-center shadow-lg transition-all ${
              isDark
                ? "bg-slate-800 border border-slate-700 text-white hover:bg-slate-700"
                : "bg-white border border-slate-200 text-slate-700 hover:bg-slate-100"
            }`}
            aria-label="Previous"
          >
            <PiArrowLeftBold className="size-4" />
          </button>

          {/* Track overflow clip */}
          <div className="overflow-hidden">
            <div
              ref={trackRef}
              className="flex transition-transform duration-500 ease-in-out"
              style={{ transform: `translateX(-${translatePct}%)` }}
            >
              {members.map((member, idx) => (
                <div
                  key={idx}
                  className="flex-shrink-0 px-3"
                  style={{ width: `${cardWidthPct}%` }}
                >
                  <div
                    className={`group text-center rounded-2xl overflow-hidden hover:shadow-md transition-all duration-300 border ${
                      isDark
                        ? "bg-slate-800/40 border-slate-800/40 hover:bg-slate-800/60"
                        : "bg-slate-50 border-slate-100/50 hover:bg-white"
                    }`}
                  >
                    {/* Image & hover social overlay */}
                    <div className="relative overflow-hidden h-72">
                      <img
                        src={member.image || "/assets/1-BcvSHoAR.jpg"}
                        alt={member.name}
                        className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                      />
                      <div className="absolute inset-0 bg-slate-900/40 opacity-0 group-hover:opacity-100 transition-opacity duration-300 flex items-end justify-center pb-6">
                        <div className="flex gap-2">
                          {member.facebook && (
                            <a href={member.facebook} target="_blank" rel="noopener noreferrer" className="size-8 rounded-full bg-blue-600 text-white flex items-center justify-center hover:bg-blue-700 cursor-pointer shadow">
                              <PiFacebookLogo className="size-4" />
                            </a>
                          )}
                          {member.instagram && (
                            <a href={member.instagram} target="_blank" rel="noopener noreferrer" className="size-8 rounded-full bg-purple-600 text-white flex items-center justify-center hover:bg-purple-700 cursor-pointer shadow">
                              <PiInstagramLogo className="size-4" />
                            </a>
                          )}
                          {member.twitter && (
                            <a href={member.twitter} target="_blank" rel="noopener noreferrer" className="size-8 rounded-full bg-sky-500 text-white flex items-center justify-center hover:bg-sky-600 cursor-pointer shadow">
                              <PiTwitterLogo className="size-4" />
                            </a>
                          )}
                          {member.linkedin && (
                            <a href={member.linkedin} target="_blank" rel="noopener noreferrer" className="size-8 rounded-full bg-blue-700 text-white flex items-center justify-center hover:bg-blue-800 cursor-pointer shadow">
                              <PiLinkedinLogo className="size-4" />
                            </a>
                          )}
                        </div>
                      </div>
                    </div>

                    {/* Name & Role */}
                    <div className="p-5">
                      <div className={`text-lg font-semibold transition-colors ${
                        isDark ? "text-white" : "text-slate-900"
                      }`}>
                        <EditableText
                          value={member.name}
                          onChange={(val) => updateMember(idx, "name", val)}
                          as="span"
                          className={isDark ? "text-white" : "text-slate-900"}
                          placeholder="Member Name"
                        />
                      </div>
                      <p className="text-slate-400 text-sm mt-1">
                        <EditableText
                          value={member.role}
                          onChange={(val) => updateMember(idx, "role", val)}
                          as="span"
                          placeholder="Member Role"
                        />
                      </p>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>

          {/* Next button */}
          <button
            onClick={handleNext}
            className={`absolute right-0 top-1/2 -translate-y-1/2 translate-x-4 z-10 size-10 rounded-full flex items-center justify-center shadow-lg transition-all ${
              isDark
                ? "bg-slate-800 border border-slate-700 text-white hover:bg-slate-700"
                : "bg-white border border-slate-200 text-slate-700 hover:bg-slate-100"
            }`}
            aria-label="Next"
          >
            <PiArrowRightBold className="size-4" />
          </button>
        </div>

        {/* Dot indicators */}
        <div className="flex justify-center gap-2 mt-8">
          {Array.from({ length: maxIndex + 1 }).map((_, idx) => (
            <button
              key={idx}
              onClick={() => { goTo(idx); startAutoPlay(); }}
              aria-label={`Go to group ${idx + 1}`}
              className={`rounded-full transition-all duration-300 ${
                idx === currentIndex
                  ? "bg-blue-600 w-6 h-2.5"
                  : isDark
                  ? "bg-slate-600 hover:bg-slate-500 w-2.5 h-2.5"
                  : "bg-slate-300 hover:bg-slate-400 w-2.5 h-2.5"
              }`}
            />
          ))}
        </div>

      </div>
    </section>
  );
}
